import React, { useMemo, useState, useEffect } from "react";
import {
	getSidebarMenuConfig,
	getFacultySidebarMenuConfig,
	type SidebarMenuItem,
	type SidebarMenuItemChild,
	type SidebarMenuLeaf,
} from "./config/menu.config";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { SidebarBranding } from "./SidebarBranding";
import { toggleSidebar } from "@store/slices/sidebarSlice";
import type { RootState } from "@store/store";
import { isFacultyUser } from "@utils/facultyAccess";
// Sidebar collapse toggle disabled for now
// import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
// import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
	SIDEBAR_MENU_ITEM_BASE,
	SIDEBAR_MENU_ITEM_COLLAPSED,
	SIDEBAR_MENU_ITEM_NESTED,
	SIDEBAR_MENU_ITEM_NESTED_DEEP,
} from "./sidebarMenuItem";
import "./Sidebar.css";

export const Sidebar: React.FC = React.memo(() => {
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const isExpanded = useSelector((state: RootState) => state.sidebar.isExpanded);
	const expandedWidth = useSelector((state: RootState) => state.sidebar.config.expandedWidth);
	const collapsedWidth = useSelector((state: RootState) => state.sidebar.config.collapsedWidth);
	const user = useSelector((state: RootState) => state.user.user);
	const token = useSelector((state: RootState) => state.user.token);
	const isFaculty = isFacultyUser(user, token);

	const menu = useMemo((): SidebarMenuItem[] => {
		return isFaculty ? getFacultySidebarMenuConfig(t) : getSidebarMenuConfig(t);
	}, [t, isFaculty]);

	const sidebarWidth = isExpanded ? expandedWidth : collapsedWidth;

	const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());

	// Auto-expand parent and nested parent when current route matches a child or grandchild route
	useEffect(() => {
		menu.forEach((item) => {
			if (item.children) {
				const activeChild = item.children.find(
					(child: SidebarMenuItemChild) =>
						(child.route && location.pathname === child.route) ||
						(child.children && child.children.some((gc: SidebarMenuLeaf) => gc.route && location.pathname === gc.route))
				);
				if (activeChild) {
					setExpandedMenus((prev) => {
						const next = new Set(prev).add(item.key);
						if (activeChild.children?.length) next.add(activeChild.key);
						return next;
					});
				}
			}
		});
	}, [location.pathname, menu]);

	// const handleToggle = () => {
	// 	dispatch(toggleSidebar());
	// };

	const toggleMenu = (key: string) => {
		setExpandedMenus((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(key)) {
				newSet.delete(key);
			} else {
				newSet.add(key);
			}
			return newSet;
		});
	};

	const isMenuExpanded = (key: string) => expandedMenus.has(key);

	const isRouteActive = (route?: string) =>
		Boolean(route && (location.pathname === route || location.pathname.startsWith(`${route}/`)));

	return (
		<>
			{/* Backdrop overlay for mobile */}
			{isExpanded && (
				<div
					className="fixed inset-0 bg-black/50 z-10 md:hidden transition-opacity duration-300"
					onClick={() => dispatch(toggleSidebar())}
					aria-hidden="true"
				/>
			)}

			{/* Sidebar collapse toggle — disabled for now
			<button
				type="button"
				onClick={handleToggle}
				className="sidebar-toggle fixed top-[12.75rem] md:top-[13.75rem] -translate-y-1/2 translate-x-1/2 w-8 h-8 rounded-full
					flex items-center justify-center shadow-lg hover:scale-110
					transition-all duration-200 z-[41] active:scale-95"
				style={{ left: `${sidebarWidth - 20}px` }}
				aria-label={isExpanded ? t("layout.sidebar.ariaLabelCollapse") : t("layout.sidebar.ariaLabelExpand")}
			>
				{isExpanded ? (
					<KeyboardDoubleArrowLeftIcon fontSize="small" />
				) : (
					<KeyboardDoubleArrowRightIcon fontSize="small" />
				)}
			</button>
			*/}

			<aside
				className={`sidebar-shell h-screen flex flex-col fixed left-0 top-0 z-20 transition-all duration-300 ease-in-out
					${isExpanded ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
				style={{
					width: `${sidebarWidth}px`,
				}}
			>

			<div className="sidebar-header w-full">
				<SidebarBranding isExpanded={isExpanded} />
			</div>

			<div className="sidebar-nav-area w-full flex-1 min-h-0">
			<nav className="sidebar-nav w-full flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-0 pb-3">
				<ul className="w-full space-y-0">
					{menu?.map(item => {
						const hasChildren = item.children && item.children.length > 0;
						const isExpandedMenu = isMenuExpanded(item.key);
						const hasActiveChild =
							hasChildren &&
							(item.children?.some((child) => isRouteActive(child.route)) ||
								item.children?.some(
									(child: SidebarMenuItemChild) =>
										child.children?.some((gc: SidebarMenuLeaf) => isRouteActive(gc.route))
								));
						const isActive = isRouteActive(item.route) || hasActiveChild;

						return (
							<li key={item.key} className="relative">
								<div className="flex flex-col">
									<button
										type="button"
										onClick={() => {
											if (hasChildren) {
												if (!isExpanded) {
													// When sidebar is collapsed -> navigate to first child
													const firstChild = item.children?.[0];
										
													if (firstChild?.route) {
														navigate(firstChild.route);
													} else if (firstChild?.children?.[0]?.route) {
														navigate(firstChild.children[0].route);
													}
												} else {
													toggleMenu(item.key);
												}
											} else if (item.route) {
												navigate(item.route);
											}
										}}
										title={!isExpanded ? item.label : undefined}
										className={`${isExpanded ? SIDEBAR_MENU_ITEM_BASE : SIDEBAR_MENU_ITEM_COLLAPSED} ${
											isActive ? "sidebar-nav-item--active" : ""
										}`}
									>
										{item?.icon}
										{isExpanded && (
											<>
												<span className="transition-opacity duration-300 opacity-100 flex-1 text-left">
													{item?.label}
												</span>
												{hasChildren && (
													<span className="sidebar-nav-chevron flex-shrink-0">
														{isExpandedMenu ? <ExpandMoreIcon /> : <ChevronRightIcon />}
													</span>
												)}
											</>
										)}
									</button>
									{/* Render children if menu is expanded (supports 3-level: child with grandchildren) */}
									{hasChildren && isExpandedMenu && isExpanded && (
										<ul className="mt-0 space-y-0">
											{item.children?.map((child) => {
												const childHasChildren = child.children && child.children.length > 0;
												const isChildExpanded = isMenuExpanded(child.key);
												const isChildActive = isRouteActive(child.route);
												const hasActiveGrandchild =
													childHasChildren &&
													child.children?.some((gc) => isRouteActive(gc.route));
												const isChildOrGrandchildActive = isChildActive || hasActiveGrandchild;

												if (childHasChildren) {
													return (
														<li key={child.key} className="relative">
															<button
																type="button"
																onClick={() => toggleMenu(child.key)}
																title={child.label}
																className={`${SIDEBAR_MENU_ITEM_NESTED} ${
																	isChildOrGrandchildActive ? "sidebar-nav-item--active" : ""
																}`}
															>
																{child?.icon}
																<span className="transition-opacity duration-300 opacity-100 flex-1 text-left">
																	{child?.label}
																</span>
																<span className="sidebar-nav-chevron flex-shrink-0">
																	{isChildExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
																</span>
															</button>
															{isChildExpanded && (
																<ul className="mt-0 space-y-0">
																	{child.children?.map((grandchild: SidebarMenuLeaf) => {
																		const isGrandchildActive = isRouteActive(grandchild.route);
																		return (
																			<li key={grandchild.key} className="relative">
																				<button
																					type="button"
																					onClick={() => grandchild.route && navigate(grandchild.route)}
																					title={grandchild.label}
																					className={`${SIDEBAR_MENU_ITEM_NESTED_DEEP} ${
																						isGrandchildActive ? "sidebar-nav-item--active" : ""
																					}`}
																				>
																					{grandchild?.icon}
																					<span className="transition-opacity duration-300 opacity-100">
																						{grandchild?.label}
																					</span>
																				</button>
																			</li>
																		);
																	})}
																</ul>
															)}
														</li>
													);
												}
												return (
													<li key={child.key} className="relative">
														<button
															type="button"
															onClick={() => child.route && navigate(child.route)}
															title={child.label}
															className={`${SIDEBAR_MENU_ITEM_NESTED} ${
																isChildActive ? "sidebar-nav-item--active" : ""
															}`}
														>
															{child?.icon}
															<span className="transition-opacity duration-300 opacity-100">
																{child?.label}
															</span>
														</button>
													</li>
												);
											})}
										</ul>
									)}
								</div>
							</li>
						);
					})}
				</ul>
			</nav>
			</div>
		</aside>
		</>
	);
});
